import React, { useState, useEffect, useRef } from 'react';

function ChooseYourPathSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section className="choose-path-section" id="choose-your-path" ref={sectionRef}>
      {/* Ambient Floating Decorative Orbs */}
      <div className="path-ambient-orb path-ambient-orb-1"></div>
      <div className="path-ambient-orb path-ambient-orb-2"></div>

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <div className={`section-title text-center path-header-anim ${isVisible ? 'header-active' : ''}`}>
          <span className="path-header-badge">
            <span className="path-badge-dot"></span>
            CHOOSE YOUR JOURNEY
          </span>
          <h2 className="section-title__title path-main-title">
            Choose Your <span className="path-title-accent">Path</span>
          </h2>
        </div>

        {/* 2 Visual Path Cards Grid */}
        <div className="row g-4 justify-content-center">
          {/* Card 2: Book an Appointment (Slides in from Right) */}
          <div className="col-xl-6 col-lg-6 col-md-12 d-flex">
            <div className={`path-card path-card-clients path-card-slide-right ${isVisible ? 'slide-active' : ''}`}>
              {/* Shimmer Light Streak Effect */}
              <div className="path-card-shimmer"></div>

              {/* Left Side: Content & Button */}
              <div className="path-card-content">
                <span className="path-tag-badge path-tag-clients">
                  <i className="fas fa-heart-circle-check me-1"></i>
                  FOR CLIENTS &amp; SEEKERS
                </span>

                <h3 className="path-card-title">
                  Book an Appointment
                </h3>

                <a href="/client-guide" className="thm-btn path-action-btn path-btn-clients">
                  <span>Explore &amp; Book Now</span>
                  <i className="fas fa-arrow-right path-btn-arrow"></i>
                </a>
              </div>

              {/* Right Side: Large Prominent Mascot Image */}
              <div className="path-img-wrapper">
                <img
                  src="/assets/images/mascot/join_as_expert.jpeg"
                  alt="Book Appointment Mascot"
                  className="path-mascot-img"
                />
              </div>
            </div>
          </div>

          {/* Card 1: Mental Health Experts (Slides in from Left) */}
          <div className="col-xl-6 col-lg-6 col-md-12 d-flex">
            <div className={`path-card path-card-experts path-card-slide-left ${isVisible ? 'slide-active' : ''}`}>
              {/* Shimmer Light Streak Effect */}
              <div className="path-card-shimmer"></div>

              {/* Left Side: Content & Button */}
              <div className="path-card-content">
                <span className="path-tag-badge path-tag-experts">
                  <i className="fas fa-user-doctor me-1"></i>
                  FOR EXPERTS &amp; PRACTITIONERS
                </span>

                <h3 className="path-card-title">
                  Mental Health Experts
                </h3>

                <a href="/mhp-guide" className="thm-btn path-action-btn path-btn-experts">
                  <span>Join as Practitioner</span>
                  <i className="fas fa-arrow-right path-btn-arrow"></i>
                </a>
              </div>

              {/* Right Side: Large Prominent Mascot Image */}
              <div className="path-img-wrapper">
                <img
                  src="/assets/images/mascot/book_appointment.jpeg"
                  alt="Mental Health Experts Mascot"
                  className="path-mascot-img"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* =========================================================
           CHOOSE YOUR PATH SECTION & SLIDING ANIMATIONS
           ========================================================= */

        .choose-path-section {
          position: relative;
          padding: 95px 0 90px;
          background: linear-gradient(180deg, #f5effe 0%, #ede3fc 50%, #f7f2fe 100%);
          overflow: hidden;
        }

        /* Ambient Floating Decorative Orbs */
        .path-ambient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(85px);
          pointer-events: none;
          z-index: 1;
          opacity: 0.55;
          animation: pathOrbFloat 14s ease-in-out infinite alternate;
        }

        .path-ambient-orb-1 {
          top: 5%;
          left: -5%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(144, 114, 200, 0.25) 0%, transparent 70%);
        }

        .path-ambient-orb-2 {
          bottom: 5%;
          right: -5%;
          width: 440px;
          height: 440px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, transparent 70%);
          animation-delay: -7s;
        }

        @keyframes pathOrbFloat {
          0% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-35px) scale(1.12) translateX(25px); }
          100% { transform: translateY(20px) scale(0.95) translateX(-20px); }
        }

        /* Header Animation */
        .path-header-anim {
          opacity: 0;
          transform: translateY(25px);
          transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .path-header-anim.header-active {
          opacity: 1;
          transform: translateY(0);
        }

        .path-header-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          color: #7c4bb9;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 7px 22px;
          border-radius: 30px;
          margin-bottom: 12px;
          box-shadow: 0 4px 15px rgba(124, 75, 185, 0.1);
          transition: all 0.35s ease;
        }

        .path-badge-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #7c4bb9;
          box-shadow: 0 0 8px #7c4bb9;
          animation: pathDotPulse 2s infinite;
        }

        @keyframes pathDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }

        .path-main-title {
          font-size: clamp(2.2rem, 4vw, 2.75rem);
          font-weight: 800;
          color: #1e1335;
          margin-top: 6px;
          margin-bottom: 45px;
          letter-spacing: -0.5px;
        }

        .path-title-accent {
          background: linear-gradient(135deg, #7c4bb9 0%, #9333ea 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          position: relative;
          display: inline-block;
        }

        /* =========================================================
           SLIDE-IN ANIMATIONS FOR THE 2 CARDS
           ========================================================= */

        /* Card 1: Slide from Left */
        .path-card-slide-left {
          opacity: 0;
          transform: translateX(-90px) scale(0.94);
          transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1),
                      border-color 0.35s ease,
                      box-shadow 0.35s ease !important;
          will-change: transform, opacity;
        }

        .path-card-slide-left.slide-active {
          opacity: 1;
          transform: translateX(0) scale(1);
        }

        /* Card 2: Slide from Right */
        .path-card-slide-right {
          opacity: 0;
          transform: translateX(90px) scale(0.94);
          transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.12s,
                      transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.12s,
                      border-color 0.35s ease,
                      box-shadow 0.35s ease !important;
          will-change: transform, opacity;
        }

        .path-card-slide-right.slide-active {
          opacity: 1;
          transform: translateX(0) scale(1);
        }

        /* Base Card Styling */
        .path-card {
          width: 100%;
          position: relative;
          background-color: #ffffff;
          border-radius: 30px;
          border: 1.5px solid #ebe2f8;
          padding: 34px 30px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          box-shadow: 0 14px 40px rgba(124, 75, 185, 0.08);
          overflow: hidden;
        }

        /* Light Streak Sweep Animation on Hover */
        .path-card-shimmer {
          position: absolute;
          top: 0;
          left: -120%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.7),
            transparent
          );
          transform: skewX(-25deg);
          transition: left 0.8s ease;
          pointer-events: none;
          z-index: 5;
        }

        .path-card:hover .path-card-shimmer {
          left: 150%;
        }

        /* Card Hover Elevation */
        .path-card.slide-active:hover {
          transform: translateY(-10px) scale(1.02) !important;
          border-color: #a855f7 !important;
          box-shadow: 0 24px 60px rgba(124, 75, 185, 0.18),
                      0 0 30px rgba(168, 85, 247, 0.12) !important;
        }

        .path-card-content {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;
          z-index: 2;
        }

        /* Tag Badges */
        .path-tag-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          padding: 5px 14px;
          border-radius: 20px;
          margin-bottom: 16px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .path-tag-experts {
          background-color: #f3e8ff;
          color: #7e22ce;
          border: 1px solid #e9d5ff;
        }

        .path-tag-clients {
          background-color: #fdf2f8;
          color: #db2777;
          border: 1px solid #fbcfe8;
        }

        .path-card:hover .path-tag-badge {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        /* Card Title */
        .path-card-title {
          font-size: 25px;
          font-weight: 800;
          color: #261145;
          margin: 0 0 24px;
          line-height: 1.28;
          transition: color 0.3s ease, transform 0.3s ease;
        }

        .path-card:hover .path-card-title {
          color: #7c4bb9;
          transform: translateX(3px);
        }

        /* Action Buttons with Dynamic Hover Effects */
        .path-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 28px !important;
          border-radius: 35px !important;
          font-size: 14.5px;
          font-weight: 700;
          color: #ffffff !important;
          text-decoration: none;
          box-shadow: 0 8px 24px rgba(124, 75, 185, 0.28) !important;
          transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
          cursor: pointer;
        }

        .path-btn-experts {
          background: linear-gradient(135deg, #7c4bb9 0%, #9333ea 100%) !important;
        }

        .path-btn-clients {
          background: linear-gradient(135deg, #9333ea 0%, #c026d3 100%) !important;
          box-shadow: 0 8px 24px rgba(192, 38, 211, 0.28) !important;
        }

        .path-btn-arrow {
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .path-action-btn:hover {
          transform: translateY(-4px) scale(1.05) !important;
          box-shadow: 0 14px 32px rgba(124, 75, 185, 0.45) !important;
        }

        .path-action-btn:hover .path-btn-arrow {
          transform: translateX(5px);
        }

        .path-action-btn:active {
          transform: translateY(-2px) scale(1.02) !important;
        }

        /* Mascot Artwork Container with Breathing Floating Animation */
        .path-img-wrapper {
          flex: 0 0 210px;
          width: 210px;
          height: 220px;
          border-radius: 24px;
          background: #f7f3fe;
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 25px rgba(124, 75, 185, 0.08);
          border: 1.5px solid #eee6fb;
          overflow: hidden;
          position: relative;
          z-index: 2;
          transition: all 0.45s cubic-bezier(0.16, 1, 0.3, 1);
          animation: pathMascotBreathing 5s ease-in-out infinite alternate;
        }

        @keyframes pathMascotBreathing {
          0% { transform: translateY(0); }
          100% { transform: translateY(-8px); }
        }

        .path-mascot-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          mix-blend-mode: multiply;
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .path-card:hover .path-img-wrapper {
          transform: scale(1.06) rotate(-1.5deg);
          border-color: #d8b4fe;
          background: #f3ebff;
          box-shadow: 0 16px 36px rgba(124, 75, 185, 0.16);
        }

        .path-card:hover .path-mascot-img {
          transform: scale(1.1) rotate(2deg);
        }

        /* Responsive */
        @media (max-width: 991px) {
          .path-card {
            padding: 28px 24px;
          }
          .path-img-wrapper {
            flex: 0 0 180px;
            width: 180px;
            height: 190px;
          }
        }

        @media (max-width: 767px) {
          .choose-path-section {
            padding: 70px 0 60px;
          }
          .path-card {
            flex-direction: column-reverse;
            text-align: center;
            padding: 26px 20px;
            gap: 18px;
          }
          .path-card-content {
            align-items: center;
          }
          .path-img-wrapper {
            width: 100%;
            max-width: 220px;
            height: 180px;
            margin-bottom: 12px;
          }
        }
      `}</style>
    </section>
  );
}

export default ChooseYourPathSection;
